import { Everything } from '@hit/amorphic-types';
import { property, supertypeClass } from '@haventech/amorphic';
import { RiskFactors } from './RiskFactors';
import { MedicalTreatment } from './MedicalTreatment';
import { Customer } from './Customer';
import {
  ActivitiesImpacted,
  CancerType,
  Cause,
  ConditionComplications,
  ConditionOnset,
  ConditionStatus,
  ConditionType,
  TreatmentType,
  SubstanceType,
  MedicalCondition as GQLMedicalCondition,
  TestType,
  TreatmentStatus,
  MeasureUnit,
  Frequency,
  TimePeriod,
  Recommendation,
  ResultsFindings,
  QualitativeResult,
} from '@hit/hit-api-client';
import { DiagnosisShort } from './DiagnosisShort';
import { MedicalPrevention } from './MedicalPrevention';

export const enum MedicalConditionType {
  MISCELLANEOUS_CONDITION = 'MISCELLANEOUS_CONDITION',
  BLOOD_TRANSFUSION_CONDITION = 'BLOOD_TRANSFUSION_CONDITION',
}
export const enum MedicalSubConditionType {
  BLADDER = 'BLADDER_CANCER',
  BREAST = 'BREAST_CANCER',
  BRAIN = 'BRAIN_CANCER',
  CERVICAL = 'CERVICAL_CANCER',
  COLON = 'COLON_CANCER',
  LEUKEMIA = 'LEUKEMIA',
  LYMPHOMA = 'LYMPHOMA',
  LUNG = 'LUNG_CANCER',
  OVARIAN = 'OVARIAN_CANCER',
  PANCREATIC = 'PANCREATIC_CANCER',
  PROSTATE = 'PROSTATE_CANCER',
  KIDNEY_RENAL = 'KIDNEY_RENAL_CANCER',
  SKIN_MELANOMA = 'SKIN_CANCER_MELANOMA',
  SKIN_OTHER = 'SKIN_CANCER_OTHER',
  TESTICULAR = 'TESTICULAR_CANCER',
  THYROID = 'THYROID_CANCER',
  OTHER = 'OTHER_CANCER',
  CELIAC_DISEASE = 'CELIAC_DISEASE',
  COLON_POLYPS = 'COLON_POLYPS',
  CHRONS_DISEASE = 'CHRONS_DISEASE',
  COLITIS_NON_ULCERATIVE = 'COLITIS_NON_ULCERATIVE',
  COLITIS_ULCERATIVE = 'COLITIS_ULCERATIVE',
  DIVERTICULITIS = 'DIVERTICULITIS',
  NAFLD_OR_NASH = 'NAFLD_OR_NASH',
  GASTRITIS = 'GASTRITIS',
  HEPITITUS_B_C_D = 'HEPITITUS_B_C_D',
  PANCREATIC_ABSCESS_CYSTS = 'PANCREATIC_ABSCESS_CYSTS',
  PANCREATITIS = 'PANCREATITIS',
  ULCER = 'ULCER',
  WEIGHT_LOSS = 'WEIGHT_LOSS',
  GERD = 'GERD',
  OTHER_DIGESTIVE = 'OTHER_DIGESTIVE',
}
@supertypeClass({ toClient: true, toServer: true })
export class MedicalCondition extends Everything {
  @property({ getType: () => Customer })
  customer: Customer;

  @property({ getType: () => RiskFactors })
  riskFactors: RiskFactors;

  @property({ type: Array, getType: () => MedicalTreatment })
  medicalTreatments: MedicalTreatment[] = [];

  @property({ type: Array, getType: () => MedicalPrevention })
  medicalPreventions: MedicalPrevention[] = [];

  get medicalTreatment(): MedicalTreatment {
    if (this.medicalTreatments && this.medicalTreatments.length > 0) {
      return this.medicalTreatments.find(
        (treatment) =>
          treatment &&
          treatment.medicalCondition &&
          treatment.medicalCondition.conditionType === this.conditionType &&
          treatment.medicalCondition.subConditionType === this.subConditionType
      );
    }
    return undefined;
  }

  set medicalTreatment(medicalTreatment: MedicalTreatment) {}

  @property()
  optionSelected: boolean = true;

  @property()
  conditionType: ConditionType | MedicalConditionType | SubstanceType;

  @property()
  subConditionType: MedicalSubConditionType | CancerType | SubstanceType;

  @property()
  conditionDescription: string;

  @property()
  conditionStage: string;

  @property()
  conditionStatus: ConditionStatus;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
  })
  conditionSpreadInd: string;

  @property()
  conditionSpreadDesc: string;

  @property()
  cause: Cause;

  @property()
  episodeInd: boolean;

  @property({ rule: ['numeric'] })
  numberEpisodesTotal: number;

  @property()
  lastEpisodeDate: Date;

  @property()
  symptomsInd: boolean;

  @property()
  conditionComplications: ConditionComplications;

  @property({ rule: ['numeric'] })
  weightChange: number;

  @property()
  weightChangeUnits: string;

  @property()
  activitiesImpacted: ActivitiesImpacted;

  @property()
  firstDiagnosisDate: Date;

  @property()
  conditionOnset: ConditionOnset;

  @property()
  substanceUsage: boolean;

  @property()
  substanceUsageType: string;

  @property()
  details: DiagnosisShort = null;

  // some unknown treatment
  get someTreatmentDetails(): MedicalTreatment {
    return this.getMedicalTreatmentByType(TreatmentType.OLI_UNKNOWN);
  }
  set someTreatment(treatment: boolean) {
    this.setMedicalTreatmentByType(this.someTreatmentDetails, treatment, TreatmentType.OLI_UNKNOWN);
  }
  get someTreatment(): boolean {
    return this.medicalTreatmentExistsByType(TreatmentType.OLI_UNKNOWN);
  }
  someTreatmentTrigger() {
    if (this.someTreatmentDetails && this.conditionType === ConditionType.MM_MEDCOND_LYMEDISEASE) {
      this.someTreatmentDetails.treatmentStatus = TreatmentStatus.OLI_TRTMNTSTAT_INPROGRESS;
    }
  }

  // CPAP
  get cpapDetails(): MedicalTreatment {
    return this.getMedicalTreatmentByType(TreatmentType.OLI_TREATTYPE_CPAP, undefined, Frequency.OLI_USAGEFREQ_PERWEEK);
  }
  set cpapTreatment(cpap: boolean) {
    this.setMedicalTreatmentByType(
      this.cpapDetails,
      cpap,
      TreatmentType.OLI_TREATTYPE_CPAP,
      undefined,
      undefined,
      Frequency.OLI_USAGEFREQ_PERWEEK
    );

    if (cpap) {
      if (this.cpapDetails) {
        this.cpapDetails.treatmentStatus = TreatmentStatus.OLI_TRTMNTSTAT_INPROGRESS;
      }
    } else {
      this.medicalTreatments = this.medicalTreatments.filter(
        (medicalTreatmentObj) =>
          medicalTreatmentObj &&
          !(
            medicalTreatmentObj.treatmentType === TreatmentType.OLI_TREATTYPE_CPAP &&
            medicalTreatmentObj.medicalCondition &&
            medicalTreatmentObj.medicalCondition._id === this._id &&
            medicalTreatmentObj.treatmentFrequencyMode === Frequency.MM_USAGEFREQ_PERNIGHT
          )
      );
    }
  }
  get cpapTreatment(): boolean {
    return this.medicalTreatmentExistsByType(
      TreatmentType.OLI_TREATTYPE_CPAP,
      undefined,
      Frequency.OLI_USAGEFREQ_PERWEEK
    );
  }

  get cpapNightlyDetails(): MedicalTreatment {
    return this.getMedicalTreatmentByType(TreatmentType.OLI_TREATTYPE_CPAP, undefined, Frequency.MM_USAGEFREQ_PERNIGHT);
  }
  set cpapNightlyTreatment(treatmentValue: string) {
    if (treatmentValue) {
      let treatmentAmt = 0;
      switch (treatmentValue) {
        case 'LESS_THAN_FOUR':
          treatmentAmt = 3;
          break;
        case 'FOUR_TO_SIX':
          treatmentAmt = 4;
          break;
        case 'MORE_THAN_SIX':
          treatmentAmt = 7;
          break;
      }
      if (this.cpapNightlyDetails) {
        this.removeMedicalTreatment(TreatmentType.OLI_TREATTYPE_CPAP, undefined, Frequency.MM_USAGEFREQ_PERNIGHT);
      }
      this.setMedicalTreatmentByType(
        this.cpapNightlyDetails,
        true,
        TreatmentType.OLI_TREATTYPE_CPAP,
        treatmentValue,
        treatmentAmt,
        Frequency.MM_USAGEFREQ_PERNIGHT
      );
      this.cpapNightlyDetails.treatmentStatus = TreatmentStatus.OLI_TRTMNTSTAT_INPROGRESS;
    } else {
      this.medicalTreatments = this.medicalTreatments.filter(
        (medicalTreatmentObj) =>
          medicalTreatmentObj &&
          !(
            medicalTreatmentObj.treatmentType === TreatmentType.OLI_TREATTYPE_CPAP &&
            medicalTreatmentObj.medicalCondition &&
            medicalTreatmentObj.medicalCondition._id === this._id &&
            medicalTreatmentObj.treatmentFrequencyMode === Frequency.MM_USAGEFREQ_PERNIGHT
          )
      );
    }
  }
  get cpapNightlyTreatment(): string {
    const treatment = this.getMedicalTreatmentByType(
      TreatmentType.OLI_TREATTYPE_CPAP,
      undefined,
      Frequency.MM_USAGEFREQ_PERNIGHT
    );
    return treatment && treatment.treatmentDesc;
  }

  // Follow up treatment
  set followupTreatment(followup: boolean) {
    this.setMedicalTreatmentByType(this.followupTreatmentDetails, followup, TreatmentType.MM_TREATTYPE_FOLLOWUP);
  }
  get followupTreatment(): boolean {
    return this.medicalTreatmentExistsByType(TreatmentType.MM_TREATTYPE_FOLLOWUP);
  }
  get followupTreatmentDetails(): MedicalTreatment {
    return this.getMedicalTreatmentByType(TreatmentType.MM_TREATTYPE_FOLLOWUP);
  }

  // Pacemaker
  get hasPacemaker(): boolean {
    return this.medicalTreatmentExistsByType(TreatmentType.MM_TREATTYPE_PACEMAKER);
  }
  set hasPacemaker(treatment: boolean) {
    this.setMedicalTreatmentByType(this.pacemakerDetails, treatment, TreatmentType.MM_TREATTYPE_PACEMAKER);
  }
  get pacemakerDetails(): MedicalTreatment {
    return this.getMedicalTreatmentByType(TreatmentType.MM_TREATTYPE_PACEMAKER);
  }

  // Surgery
  get surgeryDetails(): MedicalTreatment {
    return this.getMedicalTreatmentByType(TreatmentType.OLI_TREATTYPE_SURGERY);
  }
  get surgeryTreatment(): boolean {
    return this.medicalTreatmentExistsByType(TreatmentType.OLI_TREATTYPE_SURGERY);
  }
  set surgeryTreatment(surgery: boolean) {
    this.setMedicalTreatmentByType(this.surgeryDetails, surgery, TreatmentType.OLI_TREATTYPE_SURGERY);
  }

  // Excision
  get excisionTreatment(): boolean {
    return this.medicalTreatmentExistsByType(TreatmentType.OLI_TREATTYPE_EXCISION);
  }
  set excisionTreatment(excision: boolean) {
    this.setMedicalTreatmentByType(this.excisionTreatmentDetails, excision, TreatmentType.OLI_TREATTYPE_EXCISION);
    if (this.excisionTreatmentDetails) {
      this.excisionTreatmentDetails.treatmentStatus = TreatmentStatus.OLI_TRTMNTSTAT_COMPLETED;
    }
  }
  get excisionTreatmentDetails(): MedicalTreatment {
    return this.getMedicalTreatmentByType(TreatmentType.OLI_TREATTYPE_EXCISION);
  }

  // General Medication
  get generalMedicationTreatment(): boolean {
    return this.medicalTreatmentExistsByType(TreatmentType.OLI_TREATTYPE_MED);
  }
  set generalMedicationTreatment(medicalTreatment: boolean) {
    this.setMedicalTreatmentByType(this.generalMedicationDetails, medicalTreatment, TreatmentType.OLI_TREATTYPE_MED);
  }
  get generalMedicationDetails(): MedicalTreatment {
    return this.getMedicalTreatmentByType(TreatmentType.OLI_TREATTYPE_MED);
  }
  generalMedicationTreatmentTrigger() {
    if (
      this.generalMedicationDetails &&
      (this.conditionType === ConditionType.OLI_MEDCOND_ARTHRI ||
        this.conditionType === ConditionType.MM_MEDCOND_ELEVATEDPSA)
    ) {
      this.generalMedicationDetails.recommendation = Recommendation.PRESCRIBED;
    }
  }

  // Insulin Treamtnet
  get insulinTreatment(): boolean {
    return this.medicalTreatmentExistsByType(TreatmentType.MM_TREATTYPE_INSULIN);
  }
  set insulinTreatment(insulin: boolean) {
    this.setMedicalTreatmentByType(this.insulinTreatmentDetails, insulin, TreatmentType.MM_TREATTYPE_INSULIN);
  }
  get insulinTreatmentDetails(): MedicalTreatment {
    return this.getMedicalTreatmentByType(TreatmentType.MM_TREATTYPE_INSULIN);
  }

  // Surgically removed
  get surgicallyRemoved(): boolean {
    return this.medicalTreatmentExistsByType(TreatmentType.MM_TREATTYPE_REMOVED);
  }
  set surgicallyRemoved(wasRemoved: boolean) {
    this.setMedicalTreatmentByType(this.surgicallyRemovedDetails, wasRemoved, TreatmentType.MM_TREATTYPE_REMOVED);
    if (this.surgicallyRemovedDetails) {
      this.surgicallyRemovedDetails.treatmentStatus = TreatmentStatus.OLI_TRTMNTSTAT_COMPLETED;
    }
  }
  get surgicallyRemovedDetails(): MedicalTreatment {
    return this.getMedicalTreatmentByType(TreatmentType.MM_TREATTYPE_REMOVED);
  }

  // consulted medical Professional
  get consultedMedicalProfessionalCovid(): boolean {
    return this.medicalTreatmentExistsByType(
      TreatmentType.MM_TREATTYPE_COVID,
      undefined,
      undefined,
      TimePeriod.PAST_MONTH
    );
  }
  set consultedMedicalProfessionalCovid(consult: boolean) {
    this.setMedicalTreatmentByType(
      this.covidMedicalProfessionalDetails,
      consult,
      TreatmentType.MM_TREATTYPE_COVID,
      undefined,
      undefined,
      undefined,
      TimePeriod.PAST_MONTH
    );
  }
  get covidMedicalProfessionalDetails(): MedicalTreatment {
    return this.getMedicalTreatmentByType(
      TreatmentType.MM_TREATTYPE_COVID,
      undefined,
      undefined,
      TimePeriod.PAST_MONTH
    );
  }

  // Steroid Treatment
  get steroidTreatment(): boolean {
    return this.medicalTreatmentExistsByType(TreatmentType.MM_TREATTYPE_STEROIDS);
  }
  set steroidTreatment(steroid: boolean) {
    this.setMedicalTreatmentByType(this.steroidDetails, steroid, TreatmentType.MM_TREATTYPE_STEROIDS);
  }
  get steroidDetails(): MedicalTreatment {
    return this.getMedicalTreatmentByType(TreatmentType.MM_TREATTYPE_STEROIDS);
  }
  steroidTreatmentTrigger() {
    if (this.steroidDetails) {
      if (
        this.conditionType === ConditionType.OLI_MEDCOND_K51 &&
        this.steroidDetails.recommendation !== Recommendation.PRESCRIBED
      ) {
        this.steroidDetails.recommendation = Recommendation.PRESCRIBED;
      }
    }
  }

  // prescribed Treatment
  get prescribedTreatment(): TreatmentType {
    return this.prescribedTreatmentDetails && this.prescribedTreatmentDetails.treatmentType;
  }
  set prescribedTreatment(prescribedType: TreatmentType) {
    if (this.prescribedTreatmentDetails) {
      this.prescribedTreatmentDetails.treatmentType = prescribedType;
    } else {
      this.setMedicalTreatmentByType(
        this.prescribedTreatmentDetails,
        true,
        prescribedType,
        undefined,
        undefined,
        undefined,
        undefined,
        true
      );
    }
  }
  get prescribedTreatmentDetails(): MedicalTreatment {
    const treatment =
      this.getMedicalTreatmentByType(TreatmentType.OLI_TREATTYPE_MED) ||
      this.getMedicalTreatmentByType(TreatmentType.MM_TREATTYPE_INSULIN) ||
      this.getMedicalTreatmentByType(TreatmentType.OLI_UNKNOWN) ||
      this.getMedicalTreatmentByType(TreatmentType.MM_TREATTYPE_OTHER) ||
      this.getMedicalTreatmentByType(TreatmentType.OLI_TREATTYPE_SURGERY) ||
      this.getMedicalTreatmentByType(TreatmentType.MM_TREATTYPE_NONE);

    return treatment;
  }

  // Platelets lab test result
  get plateletsResult(): MedicalPrevention {
    return this.getMedicalPreventionByType(TestType.OLI_TESTTYPE_LABWORK, undefined, ResultsFindings.BLOOD_PLATELETS);
  }
  get plateletsMeasured(): boolean {
    return this.medicalPreventionExistsByType(
      TestType.OLI_TESTTYPE_LABWORK,
      undefined,
      ResultsFindings.BLOOD_PLATELETS
    );
  }
  set plateletsMeasured(platelets: boolean) {
    this.setMedicalPreventionByType(
      this.plateletsResult,
      platelets,
      TestType.OLI_TESTTYPE_LABWORK,
      undefined,
      undefined,
      ResultsFindings.BLOOD_PLATELETS
    );
  }

  // last scan
  get lastScanDetails(): MedicalPrevention {
    return this.getMedicalPreventionByType(TestType.OLI_TESTTYPE_XRAY);
  }
  get lastScan(): Date {
    return this.lastScanDetails && this.lastScanDetails.testDate;
  }
  set lastScan(scanDate: Date) {
    const scan = !!scanDate;
    this.setMedicalPreventionByType(this.lastScanDetails, scan, TestType.OLI_TESTTYPE_XRAY, scanDate);
  }

  // Biopsy lab test result
  get biopsyTest(): boolean {
    return this.medicalPreventionExistsByType(TestType.OLI_TESTTYPE_BIOPSY);
  }
  set biopsyTest(test: boolean) {
    this.setMedicalPreventionByType(this.biopsyResult, test, TestType.OLI_TESTTYPE_BIOPSY);
  }
  get biopsyResult(): MedicalPrevention {
    return this.getMedicalPreventionByType(TestType.OLI_TESTTYPE_BIOPSY);
  }

  // Thyroid cysts surgery - prevention tests
  get thyroidDiagnosisProcedure(): TestType | string {
    const medicalPrevention =
      this.getMedicalPreventionByType(TestType.MM_TESTTYPE_SURGERY) ||
      this.getMedicalPreventionByType(TestType.OLI_TESTTYPE_MRI) ||
      this.getMedicalPreventionByType(TestType.OLI_TESTTYPE_ULTRA) ||
      this.getMedicalPreventionByType(TestType.OLI_TESTTYPE_BIOPSY);
    return (medicalPrevention && medicalPrevention.testType) || 'NONE_OF_THESE';
  }
  set thyroidDiagnosisProcedure(testType: TestType | string) {
    if (testType !== 'NONE_OF_THESE') {
      if (this.thyroidDiagnosisResults) {
        this.thyroidDiagnosisResults.testType = testType as TestType;
      } else {
        this.setMedicalPreventionByType(this.thyroidDiagnosisResults, true, testType as TestType);
      }
    } else {
      this.medicalPreventions = this.medicalPreventions.filter(
        (medicalPreventionObj) =>
          medicalPreventionObj &&
          !(
            (medicalPreventionObj.testType === TestType.MM_TESTTYPE_SURGERY ||
              medicalPreventionObj.testType === TestType.OLI_TESTTYPE_MRI ||
              medicalPreventionObj.testType === TestType.OLI_TESTTYPE_ULTRA ||
              medicalPreventionObj.testType === TestType.OLI_TESTTYPE_BIOPSY) &&
            medicalPreventionObj.medicalCondition &&
            medicalPreventionObj.medicalCondition._id === this._id
          )
      );
    }
  }
  get thyroidDiagnosisResults(): MedicalPrevention {
    return (
      this.getMedicalPreventionByType(TestType.MM_TESTTYPE_SURGERY) ||
      this.getMedicalPreventionByType(TestType.OLI_TESTTYPE_MRI) ||
      this.getMedicalPreventionByType(TestType.OLI_TESTTYPE_ULTRA) ||
      this.getMedicalPreventionByType(TestType.OLI_TESTTYPE_BIOPSY)
    );
  }

  // Thyroid Periodic imaging tests
  get thyroidPrediodicImaging(): boolean {
    return this.medicalPreventionExistsByType(TestType.MM_TESTTYPE_PERIODICIMAGING);
  }
  set thyroidPrediodicImaging(periodicImaging: boolean) {
    this.setMedicalPreventionByType(this.thyroidImagingResults, periodicImaging, TestType.MM_TESTTYPE_PERIODICIMAGING);
    if (this.thyroidImagingResults) {
      this.thyroidImagingResults.recommendation = Recommendation.ADVISED;
    }
  }
  get thyroidImagingResults(): MedicalPrevention {
    return this.getMedicalPreventionByType(TestType.MM_TESTTYPE_PERIODICIMAGING);
  }

  // Treatment type
  get thyroidMedicalTreatment(): string {
    const treatment =
      this.getMedicalTreatmentByType(TreatmentType.OLI_TREATTYPE_SURGERY) ||
      this.getMedicalTreatmentByType(TreatmentType.OLI_TREATTYPE_MED) ||
      this.getMedicalTreatmentByType(TreatmentType.OLI_TREATTYPE_RADIATION) ||
      this.getMedicalTreatmentByType(TreatmentType.MM_TREATTYPE_RADIOACTIVEABLATION) ||
      this.getMedicalTreatmentByType(TreatmentType.MM_TREATTYPE_OTHER) ||
      this.getMedicalTreatmentByType(TreatmentType.OLI_UNKNOWN);
    return treatment && treatment.treatmentType;
  }
  set thyroidMedicalTreatment(treatmentType: TreatmentType | string) {
    if (this.thyroidMedicalTreatmentResults) {
      this.thyroidMedicalTreatmentResults.treatmentType = treatmentType as TreatmentType;
    } else {
      this.setMedicalTreatmentByType(this.thyroidMedicalTreatmentResults, true, treatmentType as TreatmentType);
    }
  }
  get thyroidMedicalTreatmentResults(): MedicalTreatment {
    return (
      this.getMedicalTreatmentByType(TreatmentType.OLI_TREATTYPE_SURGERY) ||
      this.getMedicalTreatmentByType(TreatmentType.OLI_TREATTYPE_MED) ||
      this.getMedicalTreatmentByType(TreatmentType.OLI_TREATTYPE_RADIATION) ||
      this.getMedicalTreatmentByType(TreatmentType.MM_TREATTYPE_RADIOACTIVEABLATION) ||
      this.getMedicalTreatmentByType(TreatmentType.MM_TREATTYPE_OTHER) ||
      this.getMedicalTreatmentByType(TreatmentType.OLI_UNKNOWN)
    );
  }

  // Biopsy medical prevention
  get prostateBiopsy(): boolean {
    return this.medicalPreventionExistsByType(TestType.MM_TESTTYPE_BIOPSYPROSTATE);
  }
  set prostateBiopsy(test: boolean) {
    this.setMedicalPreventionByType(this.prostateBiopsyResult, test, TestType.MM_TESTTYPE_BIOPSYPROSTATE);
  }
  get prostateBiopsyResult(): MedicalPrevention {
    return this.getMedicalPreventionByType(TestType.MM_TESTTYPE_BIOPSYPROSTATE);
  }

  // Liver Biopsy medical prevention
  get liverBiopsy(): boolean {
    return this.medicalPreventionExistsByType(TestType.MM_TESTTYPE_BIOPSYLIVER);
  }
  set liverBiopsy(test: boolean) {
    this.setMedicalPreventionByType(this.liverBiopsyResult, test, TestType.MM_TESTTYPE_BIOPSYLIVER);
  }
  get liverBiopsyResult(): MedicalPrevention {
    return this.getMedicalPreventionByType(TestType.MM_TESTTYPE_BIOPSYLIVER);
  }

  // Asthma inhaler treatment
  get hasAsthmaInhalerTreatment(): boolean {
    return this.asthmaInhalerTreatment && this.asthmaInhalerTreatment.optionSelected;
  }
  set hasAsthmaInhalerTreatment(medicalTreatment: boolean) {
    this.setMedicalTreatmentByType(
      this.asthmaInhalerTreatment,
      medicalTreatment,
      TreatmentType.MM_TREATTYPE_INHALER,
      undefined,
      undefined,
      Frequency.OLI_USAGEFREQ_PERWEEK
    );
  }
  get asthmaInhalerTreatment(): MedicalTreatment {
    return (
      this.medicalTreatments &&
      this.medicalTreatments.find(
        (treatment) =>
          treatment.treatmentType === TreatmentType.MM_TREATTYPE_INHALER &&
          treatment.medicalCondition &&
          treatment.medicalCondition._id === this._id &&
          treatment.treatmentFrequencyMode === Frequency.OLI_USAGEFREQ_PERWEEK
      )
    );
  }
  set asthmaWeeklyTreatment(weekly: boolean) {
    if (weekly && this.asthmaInhalerTreatment) {
      this.asthmaInhalerTreatment.treatmentAmt = 3;
    } else {
      if (this.asthmaInhalerTreatment) {
        this.asthmaInhalerTreatment.treatmentAmt = 0;
      }
    }
  }
  get asthmaWeeklyTreatment(): boolean {
    if (!this.asthmaInhalerTreatment || !(typeof this.asthmaInhalerTreatment.treatmentAmt === 'number')) {
      return undefined;
    }
    return (
      this.asthmaInhalerTreatment &&
      this.asthmaInhalerTreatment.treatmentFrequencyMode === Frequency.OLI_USAGEFREQ_PERWEEK &&
      this.asthmaInhalerTreatment.treatmentAmt === 3 &&
      this.asthmaInhalerTreatment.optionSelected
    );
  }
  get asthmaNightlyTreatment(): string {
    return this.asthmaInhalerNightlyTreatment && this.asthmaInhalerNightlyTreatment.treatmentFrequencyMode;
  }
  set asthmaNightlyTreatment(frequency: string) {
    if (frequency) {
      if (this.hasAsthmaInhalerTreatment) {
        if (!this.asthmaInhalerNightlyTreatment) {
          this.setMedicalTreatmentByType(
            this.asthmaInhalerNightlyTreatment,
            true,
            TreatmentType.MM_TREATTYPE_INHALER,
            undefined,
            undefined,
            frequency as Frequency
          );
        } else {
          if (this.asthmaInhalerNightlyTreatment) {
            this.asthmaInhalerNightlyTreatment.treatmentFrequencyMode = frequency as Frequency;
          }
        }
      } else {
        this.removeMedicalTreatment(TreatmentType.MM_TREATTYPE_INHALER);
        this.removeMedicalTreatment(TreatmentType.MM_TREATTYPE_INHALER, undefined, Frequency.MM_USAGEFREQ_MOSTNIGHTS);
        this.removeMedicalTreatment(TreatmentType.MM_TREATTYPE_INHALER, undefined, Frequency.MM_USAGEFREQ_EVERYWEEK);
        this.removeMedicalTreatment(
          TreatmentType.MM_TREATTYPE_INHALER,
          undefined,
          Frequency.MM_USAGEFREQ_THREETOFOURTIMESPERMONTH
        );
        this.removeMedicalTreatment(
          TreatmentType.MM_TREATTYPE_INHALER,
          undefined,
          Frequency.MM_USAGEFREQ_LESSTHANTWICEPERMONTH
        );
      }
    } else {
      this.removeMedicalTreatment(TreatmentType.MM_TREATTYPE_INHALER);
      this.removeMedicalTreatment(TreatmentType.MM_TREATTYPE_INHALER, undefined, Frequency.MM_USAGEFREQ_MOSTNIGHTS);
      this.removeMedicalTreatment(TreatmentType.MM_TREATTYPE_INHALER, undefined, Frequency.MM_USAGEFREQ_EVERYWEEK);
      this.removeMedicalTreatment(
        TreatmentType.MM_TREATTYPE_INHALER,
        undefined,
        Frequency.MM_USAGEFREQ_THREETOFOURTIMESPERMONTH
      );
      this.removeMedicalTreatment(
        TreatmentType.MM_TREATTYPE_INHALER,
        undefined,
        Frequency.MM_USAGEFREQ_LESSTHANTWICEPERMONTH
      );
    }
  }
  get asthmaInhalerNightlyTreatment(): MedicalTreatment {
    return (
      this.medicalTreatments &&
      this.medicalTreatments.find(
        (treatment) =>
          treatment.treatmentType === TreatmentType.MM_TREATTYPE_INHALER &&
          treatment.medicalCondition &&
          treatment.medicalCondition._id === this._id &&
          (treatment.treatmentFrequencyMode === Frequency.MM_USAGEFREQ_MOSTNIGHTS ||
            treatment.treatmentFrequencyMode === Frequency.MM_USAGEFREQ_EVERYWEEK ||
            treatment.treatmentFrequencyMode === Frequency.MM_USAGEFREQ_THREETOFOURTIMESPERMONTH ||
            treatment.treatmentFrequencyMode === Frequency.MM_USAGEFREQ_LESSTHANTWICEPERMONTH)
      )
    );
  }
  // Overnight asthma hospitalization
  get overnightAsthmaHospitalization(): boolean {
    return this.medicalTreatmentExistsByType(
      TreatmentType.OLI_TREATTYPE_HOSP,
      undefined,
      undefined,
      TimePeriod.PAST_THREE_YEARS
    );
  }
  set overnightAsthmaHospitalization(medicalTreatment: boolean) {
    this.setMedicalTreatmentByType(
      this.overnightHospDetails,
      medicalTreatment,
      TreatmentType.OLI_TREATTYPE_HOSP,
      undefined,
      undefined,
      undefined,
      TimePeriod.PAST_THREE_YEARS
    );
  }
  get overnightAsthmaHospDetails(): MedicalTreatment {
    return this.getMedicalTreatmentByType(
      TreatmentType.OLI_TREATTYPE_HOSP,
      undefined,
      undefined,
      TimePeriod.PAST_THREE_YEARS
    );
  }
  // Emergency Asthma hospitalization
  get emergencyAsthmaHospitalization(): boolean {
    return this.medicalTreatmentExistsByType(
      TreatmentType.MM_TREATTYPE_EMERGENCYDEPT,
      undefined,
      undefined,
      TimePeriod.WITHIN_ONE_YEAR
    );
  }
  set emergencyAsthmaHospitalization(medicalTreatment: boolean) {
    this.setMedicalTreatmentByType(
      this.emergencyHospDetails,
      medicalTreatment,
      TreatmentType.MM_TREATTYPE_EMERGENCYDEPT,
      undefined,
      undefined,
      undefined,
      TimePeriod.WITHIN_ONE_YEAR
    );
  }
  get emergencyAsthmaHospDetails() {
    return this.getMedicalTreatmentByType(
      TreatmentType.MM_TREATTYPE_EMERGENCYDEPT,
      undefined,
      undefined,
      TimePeriod.WITHIN_ONE_YEAR
    );
  }

  // Overnight hospitalization
  get overnightHospitalization(): boolean {
    return this.medicalTreatmentExistsByType(TreatmentType.OLI_TREATTYPE_HOSP);
  }
  set overnightHospitalization(medicalTreatment: boolean) {
    this.setMedicalTreatmentByType(this.overnightHospDetails, medicalTreatment, TreatmentType.OLI_TREATTYPE_HOSP);
  }
  get overnightHospDetails(): MedicalTreatment {
    return this.getMedicalTreatmentByType(TreatmentType.OLI_TREATTYPE_HOSP);
  }

  // Emergency hospitalization
  get emergencyHospitalization(): boolean {
    return this.medicalTreatmentExistsByType(TreatmentType.MM_TREATTYPE_EMERGENCYDEPT);
  }
  set emergencyHospitalization(medicalTreatment: boolean) {
    this.setMedicalTreatmentByType(
      this.emergencyHospDetails,
      medicalTreatment,
      TreatmentType.MM_TREATTYPE_EMERGENCYDEPT
    );
  }
  get emergencyHospDetails() {
    return this.getMedicalTreatmentByType(TreatmentType.MM_TREATTYPE_EMERGENCYDEPT);
  }

  // Covid lab test result
  get covidTest(): string {
    return this.covidTestResults && this.covidTestResults.resultsType;
  }
  set covidTest(covidTestResults: string) {
    if (this.covidTestResults) {
      this.removeMedicalPrevention(
        undefined,
        TestType.MM_TESTTYPE_COVID,
        covidTestResults === QualitativeResult.OLI_QUALVALUE_POSITIVE
          ? QualitativeResult.OLI_QUALVALUE_NEGATIVE
          : QualitativeResult.OLI_QUALVALUE_POSITIVE
      );
    }
    this.setMedicalPreventionByType(
      this.covidTestResults,
      true,
      TestType.MM_TESTTYPE_COVID,
      undefined,
      covidTestResults as QualitativeResult
    );
  }
  get covidTestResults(): MedicalPrevention {
    return (
      this.getMedicalPreventionByType(TestType.MM_TESTTYPE_COVID, QualitativeResult.OLI_QUALVALUE_POSITIVE) ||
      this.getMedicalPreventionByType(TestType.MM_TESTTYPE_COVID, QualitativeResult.OLI_QUALVALUE_NEGATIVE)
    );
  }
  get currentCovidTreatment(): string {
    return this.someTreatmentDetails && this.someTreatmentDetails.treatmentStatus;
  }
  set currentCovidTreatment(treatmentStatus: string) {
    const createTreatment = treatmentStatus ? true : false;
    this.setMedicalTreatmentByType(this.someTreatmentDetails, createTreatment, TreatmentType.OLI_UNKNOWN);
    if (this.someTreatment) {
      this.conditionComplications = ConditionComplications.ANY;
      this.someTreatmentDetails.treatmentStatus = treatmentStatus as TreatmentStatus;
    }
    if (!this.someTreatmentDetails.optionSelected && this.conditionComplications === ConditionComplications.ANY) {
      this.conditionComplications = null;
    }
  }
  get covidGeneralMedication(): boolean {
    return this.generalMedicationTreatment;
  }
  set covidGeneralMedication(covidGeneralMedication: boolean) {
    this.setMedicalTreatmentByType(
      this.generalMedicationDetails,
      covidGeneralMedication,
      TreatmentType.OLI_TREATTYPE_MED
    );
    if (this.generalMedicationDetails) {
      this.generalMedicationDetails.timePeriod = TimePeriod.PAST_MONTH;
    }
  }

  // set colonoscopy date
  get colonoscopyDate(): Date {
    const result = this.getMedicalPreventionByType(TestType.OLI_TESTTYPE_COLON);
    return result && result.testDate;
  }
  set colonoscopyDate(colonoscopyDate: Date) {
    if (colonoscopyDate) {
      this.medicalPreventions.push(
        MedicalPrevention.new(this.riskFactors, this, true, TestType.OLI_TESTTYPE_COLON, colonoscopyDate)
      );
    } else {
      this.medicalPreventions = this.medicalPreventions.filter(
        (medPreventionObj) => medPreventionObj && !(medPreventionObj.testType === TestType.OLI_TESTTYPE_COLON)
      );
    }
  }
  // Colonoscopy result
  get colonoscopyResult(): MedicalPrevention {
    return this.getMedicalPreventionByType(TestType.OLI_TESTTYPE_COLON);
  }

  // Substance abuse
  get substanceUsageAlcohol(): boolean {
    return this.substanceUsage;
  }
  set substanceUsageAlcohol(substanceUsage: boolean) {
    this.substanceUsage = substanceUsage;
    this.substanceUsageType = substanceUsage ? SubstanceType.OLI_SUBSTANCE_ALCOHOL : null;
  }

  get previousHospitalization(): string {
    const medTreatment = this.getMedicalTreatmentByType(TreatmentType.OLI_TREATTYPE_HOSP);
    if (!medTreatment) {
      return;
    }
    if (medTreatment && medTreatment.timePeriod === TimePeriod.PAST_TEN_YEARS) {
      return TimePeriod.PAST_TEN_YEARS;
    } else if (medTreatment && medTreatment.timePeriod && medTreatment.timePeriod !== TimePeriod.NEVER) {
      return 'PAST_YEARS';
    } else {
      return TimePeriod.NEVER;
    }
  }
  set previousHospitalization(hospTerm: string) {
    const createHosp = hospTerm === 'PAST_YEARS' || hospTerm === TimePeriod.PAST_TEN_YEARS ? true : false;
    let pastYears;
    if (hospTerm === TimePeriod.PAST_TEN_YEARS) {
      pastYears = TimePeriod.PAST_TEN_YEARS;
    } else if (hospTerm === 'PAST_YEARS') {
      pastYears = 'PAST_YEARS';
    } else {
      pastYears = TimePeriod.NEVER;
    }
    // pastYears = hospTerm === TimePeriod.PAST_TEN_YEARS ? TimePeriod.PAST_TEN_YEARS : undefined;
    if (this.overnightHospDetails) {
      this.medicalTreatments.forEach((medicalTreatmentObj, index) => {
        if (
          medicalTreatmentObj.treatmentType === TreatmentType.OLI_TREATTYPE_HOSP &&
          medicalTreatmentObj.medicalCondition &&
          medicalTreatmentObj.medicalCondition._id === this._id
        ) {
          this.medicalTreatments[index].optionSelected = false;
          this.medicalTreatments[index].timePeriod = pastYears;
        }
      });
    }
    this.setMedicalTreatmentByType(
      this.overnightHospDetails,
      createHosp,
      TreatmentType.OLI_TREATTYPE_HOSP,
      undefined,
      undefined,
      undefined,
      pastYears
    );
  }

  constructor() {
    super();
  }

  static newRiskFactors(
    riskFactors: RiskFactors,
    conditionType: ConditionType | MedicalConditionType | SubstanceType,
    subConditionType: MedicalSubConditionType | CancerType | SubstanceType,
    treatmentType?: TreatmentType,
    optionSelected?: boolean
  ) {
    return new MedicalCondition().initRiskFactors(
      riskFactors,
      conditionType,
      subConditionType,
      treatmentType,
      optionSelected
    );
  }

  initRiskFactors(
    riskFactors: RiskFactors,
    conditionType: ConditionType | MedicalConditionType | SubstanceType,
    subConditionType: MedicalSubConditionType | CancerType | SubstanceType,
    treatmentType?: TreatmentType,
    optionSelected?: boolean
  ) {
    this.riskFactors = riskFactors;
    this.subConditionType = subConditionType;
    this.conditionType = conditionType;
    this.customer = riskFactors.customer;
    if (treatmentType && treatmentType === TreatmentType.OLI_TREATTYPE_SURGERY) {
      this.setMedicalTreatmentByType(this.surgeryDetails, true, TreatmentType.OLI_TREATTYPE_SURGERY);
    }
    this.optionSelected = typeof optionSelected === 'boolean' ? optionSelected : this.optionSelected;
    return this;
  }

  //arthiritis
  showDiagnosisForOsteoarthritis() {
    return this.generalMedicationTreatment; //|| this.hasBMDDisabilityBenefits;
  }

  // Medical Treatment
  getMedicalTreatmentByType(
    treatmentType: TreatmentType,
    treatmentDesc?: TreatmentType,
    frequency?: Frequency,
    timePeriod?: TimePeriod
  ): MedicalTreatment {
    return (
      this.medicalTreatments &&
      this.medicalTreatments.find(
        (treatment) =>
          treatment.treatmentType === treatmentType &&
          treatment.medicalCondition &&
          treatment.medicalCondition._id === this._id &&
          treatment.treatmentDesc === (treatmentDesc ? treatmentDesc : treatment.treatmentDesc) &&
          treatment.treatmentFrequencyMode === (frequency ? frequency : treatment.treatmentFrequencyMode) &&
          treatment.timePeriod === (timePeriod ? timePeriod : treatment.timePeriod)
      )
    );
  }
  medicalTreatmentExistsByType(
    treatmentType: TreatmentType,
    treatmentDesc?: TreatmentType,
    frequency?: Frequency,
    timePeriod?: TimePeriod
  ): boolean {
    const medicalTreatment = this.getMedicalTreatmentByType(treatmentType, treatmentDesc, frequency, timePeriod);
    return medicalTreatment && medicalTreatment.optionSelected;
  }
  setMedicalTreatmentByType(
    medicalTreatment: MedicalTreatment,
    treatment: boolean,
    treatmentType: TreatmentType,
    description?: string,
    treatmentAmt?: number,
    frequency?: Frequency,
    timePeriod?: TimePeriod,
    singleTreatment?: boolean
  ) {
    if (medicalTreatment) {
      this.removeMedicalTreatment(
        treatmentType,
        description ? description : medicalTreatment.treatmentDesc,
        frequency ? frequency : medicalTreatment.treatmentFrequencyMode,
        timePeriod ? timePeriod : medicalTreatment.timePeriod
      );
    }
    if (singleTreatment) {
      this.medicalTreatments = [];
    }
    this.medicalTreatments.push(
      MedicalTreatment.new(
        this.riskFactors,
        this,
        treatment,
        treatmentType,
        description,
        treatmentAmt,
        frequency,
        timePeriod
      )
    );
  }

  removeMedicalTreatment(
    treatmentType: TreatmentType,
    description?: string,
    frequency?: Frequency,
    timePeriod?: TimePeriod
  ) {
    this.medicalTreatments = this.medicalTreatments.filter(
      (medicalTreatmentObj) =>
        medicalTreatmentObj &&
        !(
          medicalTreatmentObj.treatmentType === treatmentType &&
          medicalTreatmentObj.medicalCondition &&
          medicalTreatmentObj.medicalCondition._id === this._id &&
          (medicalTreatmentObj.treatmentDesc === description || (!description && !medicalTreatmentObj.treatmentDesc)) &&
          (medicalTreatmentObj.treatmentFrequencyMode === frequency ||
            (!frequency && !medicalTreatmentObj.treatmentFrequencyMode)) &&
          (medicalTreatmentObj.timePeriod === timePeriod || (!timePeriod && !medicalTreatmentObj.timePeriod))
        )
    );
  }

  // medical Prevention by type
  getMedicalPreventionByType(
    testType: TestType,
    results?: QualitativeResult,
    resultsFindings?: ResultsFindings
  ): MedicalPrevention {
    return (
      this.medicalPreventions &&
      this.medicalPreventions.find(
        (test) =>
          test.testType === testType &&
          (!results || test.resultsType === results) &&
          (!resultsFindings || test.resultsFindings === resultsFindings) &&
          test.medicalCondition &&
          test.medicalCondition._id === this._id
      )
    );
  }
  medicalPreventionExistsByType(
    testCode: TestType,
    results?: QualitativeResult,
    resultsFindings?: ResultsFindings
  ): boolean {
    const prevention = this.getMedicalPreventionByType(testCode, results, resultsFindings);
    return prevention && prevention.optionSelected;
  }
  setMedicalPreventionByType(
    medicalPrevention: MedicalPrevention,
    optionSelected: boolean,
    testType: TestType,
    testDate?: Date,
    resultsType?: QualitativeResult,
    resultsFindings?: ResultsFindings
  ) {
    if (medicalPrevention) {
      this.removeMedicalPrevention(
        resultsFindings ? resultsFindings : medicalPrevention.resultsFindings,
        testType ? testType : medicalPrevention.testType,
        resultsType ? resultsType : medicalPrevention.resultsType
      );
    }
    const prevention = MedicalPrevention.new(
      this.riskFactors,
      this,
      optionSelected,
      testType,
      testDate,
      resultsFindings,
      resultsType
    );
    this.medicalPreventions.push(prevention);
  }

  removeMedicalPrevention(resultsFindings: ResultsFindings, testType: TestType, resultsType?: QualitativeResult) {
    this.medicalPreventions = this.medicalPreventions.filter(
      (medicalPreventionObj) =>
        medicalPreventionObj &&
        !(
          (medicalPreventionObj.resultsFindings === resultsFindings ||
            (!medicalPreventionObj.resultsFindings && !resultsFindings)) &&
          (medicalPreventionObj.testType === testType || (!medicalPreventionObj.testType && !testType)) &&
          (medicalPreventionObj.resultsType === resultsType || (!medicalPreventionObj.resultsType && !resultsType)) &&
          medicalPreventionObj.medicalCondition &&
          medicalPreventionObj.medicalCondition._id === this._id
        )
    );
  }
}
